import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ResponseApi } from '../core/models/response-api';
import { environment } from '../../environments/environment';
import { AppUtil } from '../core/utility/app-util';

export interface TransaccionEntity {
    correct: boolean;
    id: string;
    message: string;
}

export interface CarritoItemRequest {
    productoUuid: string;
    cantidad: number;
    fechaEntrega: string;
    rangoHorarioUuid: string;
    complementoUuids: string[];
}

export interface CarritoItemResponse {
    uuid: string;
    productoUuid: string;
    nombre: string;
    cantidad: number;
    precioUnitario: number;
    precioTotal: number;
    fechaEntrega: string | null;
    rangoHorarioUuid: string | null;
    urlImagen: string | null;
    complementos: string[];
}

export interface CarritoResponse {
    uuid: string;
    sesionToken: string | null;
    checkoutEstado: string | null;
    establecimientoId: string | null;
    establecimientoNombre: string | null;
    costoEnvio: number | null;
    expiraAt: string | null;
    segundosRestantes: number;
    items: CarritoItemResponse[];
    subtotal: number;
}

export interface EstablecimientoDisponibleApi {
    uuid: string;
    nombre: string;
    direccion: string | null;
    telefono: string | null;
    latitud: number | null;
    longitud: number | null;
    disponible: number;
    tieneDelivery: boolean;
    precioDelivery: number | null;
}

export interface ReservaApi {
    establecimientoId: string;
    establecimientoNombre: string;
    costoEnvio: number | null;
    expiraAt: string;
    segundosRestantes: number;
}

export interface CarritoCheckoutPayload {
    estado?: string;
    tipoComprobanteId?: number | null;
    tipoDocumentoId?: number | null;
    numeroDocumento?: string | null;
    nombre?: string | null;
    apellidos?: string | null;
    razonSocial?: string | null;
    correo?: string | null;
    telefono?: string | null;
    tipoEntregaId?: number | null;
    tipoDestinatarioId?: number | null;
    destinatarioNombre?: string | null;
    destinatarioApellidos?: string | null;
    destinatarioCelular?: string | null;
    distrito?: string | null;
    direccion?: string | null;
    referencia?: string | null;
    latitud?: number | null;
    longitud?: number | null;
    empresaMetodoPagoUuid?: string | null;
}

export interface CarritoReservarPayload {
    tipoEntregaId: number;
    establecimientoId?: string | null;
    latitud?: number | null;
    longitud?: number | null;
}

@Injectable({
    providedIn: 'root'
})
export class CarritoBackService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = AppUtil.addTrailingSlash(environment.URL_API_PEDIDOS) + 'api/carrito';

    createCarrito(): Observable<ResponseApi<CarritoResponse>> {
        return this.http.post<ResponseApi<CarritoResponse>>(`${this.baseUrl}/create-carrito`, {});
    }

    getCarrito(carritoUuid: string): Observable<ResponseApi<CarritoResponse>> {
        return this.http.get<ResponseApi<CarritoResponse>>(`${this.baseUrl}/get-carrito/${carritoUuid}`);
    }

    claimCarrito(carritoUuid: string): Observable<ResponseApi<CarritoResponse>> {
        return this.http.post<ResponseApi<CarritoResponse>>(`${this.baseUrl}/claim-carrito/${carritoUuid}`, {});
    }

    addItem(carritoUuid: string, item: CarritoItemRequest): Observable<ResponseApi<TransaccionEntity>> {
        return this.http.post<ResponseApi<TransaccionEntity>>(`${this.baseUrl}/add-item/${carritoUuid}`, item);
    }

    updateItem(
        carritoUuid: string,
        itemUuid: string,
        item: CarritoItemRequest
    ): Observable<ResponseApi<TransaccionEntity>> {
        return this.http.put<ResponseApi<TransaccionEntity>>(
            `${this.baseUrl}/update-item/${carritoUuid}/${itemUuid}`,
            item
        );
    }

    removeItem(carritoUuid: string, itemUuid: string): Observable<ResponseApi<TransaccionEntity>> {
        return this.http.delete<ResponseApi<TransaccionEntity>>(
            `${this.baseUrl}/remove-item/${carritoUuid}/${itemUuid}`
        );
    }

    listarEstablecimientos(carritoUuid: string): Observable<ResponseApi<EstablecimientoDisponibleApi[]>> {
        return this.http.get<ResponseApi<EstablecimientoDisponibleApi[]>>(
            `${this.baseUrl}/listar-establecimientos/${carritoUuid}`
        );
    }

    guardarCheckout(carritoUuid: string, body: CarritoCheckoutPayload): Observable<ResponseApi<TransaccionEntity>> {
        return this.http.post<ResponseApi<TransaccionEntity>>(`${this.baseUrl}/guardar-checkout/${carritoUuid}`, body);
    }

    reservar(carritoUuid: string, body: CarritoReservarPayload): Observable<ResponseApi<ReservaApi>> {
        return this.http.post<ResponseApi<ReservaApi>>(`${this.baseUrl}/reservar/${carritoUuid}`, body);
    }

    liberarReserva(carritoUuid: string): Observable<ResponseApi<TransaccionEntity>> {
        return this.http.post<ResponseApi<TransaccionEntity>>(`${this.baseUrl}/liberar-reserva/${carritoUuid}`, {});
    }
}
